/**
 * Smooth open/close for a group of <details> rows.
 *
 * The CSS-only route (`::details-content` plus `interpolate-size`) only works
 * in very recent Chrome. Everywhere else the rule is dropped and the row snaps
 * open, which is what makes the page lurch. This animates the element's height
 * directly, so it behaves the same in every browser.
 *
 * Closing is the reason this needs script at all: setting `open = false`
 * removes the content immediately, so there is nothing left to animate. The
 * row is held open until the animation finishes, then closed.
 *
 * With JavaScript off the rows still work — they just open instantly, which is
 * the native behaviour.
 */
const DURATION = 280;

class FaqAccordion extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const summary = event.target.closest('summary');
      if (!summary || !this.contains(summary)) return;

      const details = summary.parentElement;
      if (!(details instanceof HTMLDetailsElement)) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      event.preventDefault();
      this.toggle(details, summary);
    });
  }

  toggle(details, summary) {
    // Ignore clicks landing mid-animation rather than queueing a second one.
    if (details.dataset.animating) return;

    const content = summary.nextElementSibling;
    if (!content) {
      details.open = !details.open;
      return;
    }

    const opening = !details.open;
    const from = details.offsetHeight;

    // Held open either way: closing needs the content measurable and visible
    // for the whole animation.
    details.open = true;

    // Summary plus content, measured directly. `details.scrollHeight` reports
    // the collapsed height if it is read before the newly-opened content has
    // been laid out, which leaves the row animating to — and stuck at — its
    // shut height with the answer clipped inside it.
    const to = opening ? summary.offsetHeight + content.offsetHeight : summary.offsetHeight;

    if (!Number.isFinite(to) || to <= 0) {
      details.open = opening;
      delete details.dataset.animating;
      return;
    }

    details.dataset.animating = 'true';

    const animation = details.animate(
      { height: [`${from}px`, `${to}px`] },
      { duration: DURATION, easing: 'ease' }
    );

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      if (animation.playState === 'running') animation.cancel();
      details.open = opening;
      details.style.height = '';
      delete details.dataset.animating;
    };

    animation.onfinish = animation.oncancel = finish;

    // Backstop. The row is clipped while it animates, so an animation that
    // never reports finishing would strand it at its collapsed height with the
    // answer sealed inside — unreadable and unreachable. Browsers throttle
    // animations in background and occluded tabs, so this is not hypothetical:
    // correctness must not depend on the animation completing.
    setTimeout(finish, DURATION + 150);
  }
}

if (!customElements.get('faq-accordion')) {
  customElements.define('faq-accordion', FaqAccordion);
}
